<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useCreditMessagesReady } from '../composables/useCreditMessagesReady'
import {
  canManageSubscription,
  normalizePaymentFailure,
  normalizePlanName,
  paymentFailureStorageKey,
  subscriptionField,
  type PaymentFailureNotice,
} from '../service/subscriptionPresentation'

import { useAccount, useRbac } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert, Button } from '@/shared/ui'

const account = useAccount()
const rbac = useRbac()
const route = useRoute()
const { t } = useI18n()
const format = useFormat()
const messagesReady = useCreditMessagesReady('credits.paymentFailure.pendingTitle')

type SuccessNotice = Omit<PaymentFailureNotice, 'state'> & { state: 'success' }
type BannerNotice = PaymentFailureNotice | SuccessNotice

const successNotice = ref<SuccessNotice | null>(null)
const activeAccountId = ref('')
const paymentFailure = computed(() => normalizePaymentFailure(account.accountDetail.value))
const notice = computed<BannerNotice | null>(() => paymentFailure.value || successNotice.value)
const canManage = computed(() =>
  canManageSubscription({
    accountUserId: account.accountUserId.value,
    userId: account.userId.value,
    selectedAccountLevel: account.accountLevel.value,
    isSysAdmin: rbac.isSysAdmin.value,
  }),
)
const title = computed(() =>
  notice.value?.state === 'success'
    ? t('credits.paymentFailure.successTitle')
    : notice.value?.state === 'downgraded_to_free'
      ? t('credits.paymentFailure.downgradedTitle')
      : t('credits.paymentFailure.pendingTitle'),
)
const message = computed(() => {
  const current = notice.value
  if (!current) return ''
  if (current.state === 'success') {
    return t('credits.paymentFailure.successDescription', { plan: current.planName })
  }
  if (current.state === 'downgraded_to_free') {
    return t('credits.paymentFailure.downgradedDescription', {
      plan: current.planName || t('credits.paymentFailure.paidPlan'),
    })
  }
  return current.downgradeAt
    ? t('credits.paymentFailure.pendingWithDate', {
        date: format.formatDate(current.downgradeAt, { dateStyle: 'long' }),
      })
    : t('credits.paymentFailure.pendingDescription')
})

function openBillingPortal(): void {
  const url = notice.value?.portalUrl
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function readStoredFailure(key: string): { planName?: string } | null {
  try {
    const value = window.sessionStorage.getItem(key)
    return value ? (JSON.parse(value) as { planName?: string }) : null
  } catch {
    return null
  }
}

function storeFailure(key: string, failure: PaymentFailureNotice): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ planName: failure.planName }))
  } catch {
    // The active failure banner still works when storage is unavailable.
  }
}

function consumeStoredFailure(key: string): { planName?: string } | null {
  const stored = readStoredFailure(key)
  if (!stored) return null
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // The one-time in-memory success notice still works.
  }
  return stored
}

watch(
  [account.accountId, account.accountDetail, paymentFailure],
  ([accountId, detail, failure]) => {
    const normalizedId = String(accountId || '')
    if (normalizedId !== activeAccountId.value) {
      activeAccountId.value = normalizedId
      successNotice.value = null
    }
    if (!normalizedId || !detail) return

    const storageKey = paymentFailureStorageKey(normalizedId)
    if (failure) {
      successNotice.value = null
      storeFailure(storageKey, failure)
      return
    }
    if (successNotice.value) return

    const stored = consumeStoredFailure(storageKey)
    if (!stored) return
    const currentPlan = normalizePlanName(
      subscriptionField(detail, ['plan_name', 'package_name', 'current_plan_name']),
    )
    successNotice.value = {
      state: 'success',
      downgradeAt: null,
      portalUrl: '',
      planName: currentPlan || stored.planName || t('credits.paymentFailure.paidPlan'),
    }
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  (path, previous) => {
    if (previous !== undefined && path !== previous) successNotice.value = null
  },
)
</script>

<template>
  <Alert
    v-if="notice && messagesReady"
    :tone="
      notice.state === 'success'
        ? 'success'
        : notice.state === 'downgraded_to_free'
          ? 'warning'
          : 'danger'
    "
    :title="title"
    data-testid="account-payment-banner"
  >
    <span>{{ message }}</span>
    <span v-if="notice.state !== 'success' && !canManage">
      {{ t('credits.paymentFailure.adminRequired') }}</span
    >
    <Button
      v-if="notice.state !== 'success' && notice.portalUrl && canManage"
      class="ms-sm"
      size="sm"
      variant="outline"
      @click="openBillingPortal"
    >
      {{ t('credits.paymentFailure.manageBilling') }}
    </Button>
  </Alert>
</template>
